import React from 'react'
import { Box, Button, IconButton, TextField, Typography } from '@mui/material'

import GoogleIcon from '../../../../components/icon/GoogleIcon'
import { formatQuantity, toSafeNumber } from '../../../../helpers/utils'
import {
  UI_TEXT_EMPTY_VALUE,
  UI_TEXT_NO_BARCODE,
  UI_TEXT_NO_CODE,
  UI_TEXT_VALID_QUANTITY
} from '../../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { getPrimaryBarcode } from '../../helpers/refundScanModalUtils'
import {
  UI_COLOR_BORDER_INFO_SOFT,
  UI_COLOR_DANGER_TEXT,
  UI_COLOR_ICON_GRAY,
  UI_COLOR_PRIMARY,
  UI_COLOR_SUCCESS_ALT,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_TEXT_INK_ALT,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_TEXT_TERTIARY
} from '../../../../constants/uiColors'

const RefundScanSelectedLinePanel = ({
  selectedLine,
  selectedLineStatus,
  selectedOriginalQuantity,
  selectedUnit,
  selectedVerifiedQuantity,
  verifiedQuantity,
  allowsDecimals = false,
  quantityError,
  quantityIsValid,
  isEditMode,
  isUpdatingLine,
  hideScanControls,
  processedNoticeLabel,
  onStartNewScan,
  onVerifiedQuantityChange
}) => {
  const primaryBarcode = getPrimaryBarcode(selectedLine.product_barcode)

  return (
    <>
      {isUpdatingLine && !isEditMode && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 14,
              fontWeight: 800,
              color: UI_COLOR_TEXT_INK_ALT,
              textTransform: 'uppercase'
            }}
          >
            <GoogleIcon name="info" size={16} color={UI_COLOR_TEXT_INK_ALT} />
            {processedNoticeLabel}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: UI_COLOR_TEXT_TERTIARY }}>
            {REFUND_APPROVAL_TEXT.updateQuantityHintPrefix}{' '}
            <Button
              variant="text"
              onClick={onStartNewScan}
              sx={{
                minWidth: 0,
                p: 0,
                color: UI_COLOR_PRIMARY,
                fontSize: 13,
                lineHeight: 'inherit',
                textDecoration: 'underline',
                textTransform: 'none',
                verticalAlign: 'baseline',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              {REFUND_APPROVAL_TEXT.startNewScan}
            </Button>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pr: { xs: 2, sm: isEditMode ? 2.5 : 6 },
          py: 2,
          border: `1px solid ${UI_COLOR_BORDER_INFO_SOFT}`,
          borderRadius: 2,
          backgroundColor: UI_COLOR_SURFACE_INFO,
          position: 'relative'
        }}
      >
        {!isEditMode && !hideScanControls && (
          <IconButton
            size="small"
            aria-label={REFUND_APPROVAL_TEXT.clearScannedProductAria}
            onClick={onStartNewScan}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: { xs: 'none', sm: 'inline-flex' }
            }}
          >
            <GoogleIcon name="close" size={18} color={UI_COLOR_ICON_GRAY} />
          </IconButton>
        )}
        <Typography
          sx={{
            fontSize: { xs: 13, sm: 13 },
            fontWeight: 800,
            lineHeight: 1.25,
            textTransform: 'uppercase'
          }}
        >
          {selectedLine.product_name}
        </Typography>
        <Typography sx={{ fontSize: 13, mt: 0.75 }}>
          {selectedLine.product_db_ref || UI_TEXT_NO_CODE} - {primaryBarcode || UI_TEXT_NO_BARCODE}
        </Typography>

        <Box
          sx={{
            display: { xs: 'grid', sm: 'flex' },
            gridTemplateColumns: { xs: 'auto auto auto', sm: 'none' },
            alignItems: 'center',
            columnGap: { xs: 1, sm: 2 },
            rowGap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            mt: 2
          }}
        >
          <Box
            component="span"
            className={`order-status ${selectedLineStatus.className}`}
            sx={{
              boxSizing: 'border-box',
              minWidth: { xs: 96, sm: 96 },
              width: { xs: 92, sm: 'auto' },
              height: 26,
              px: { xs: 1, sm: 1.5 },
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              fontSize: { xs: 11, sm: 11 },
              fontWeight: 700,
              lineHeight: 1
            }}
          >
            {selectedLineStatus.label}
          </Box>
          <Typography sx={{ fontSize: { xs: 12, sm: 12 }, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {REFUND_APPROVAL_TEXT.originalQtyLabel}{' '}
            <strong>{formatQuantity(selectedOriginalQuantity)}</strong> {selectedUnit}
          </Typography>
          <Typography sx={{ fontSize: { xs: 12, sm: 12 }, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {REFUND_APPROVAL_TEXT.verifiedQtyLabel}{' '}
            <strong>
              {selectedVerifiedQuantity != null
                ? formatQuantity(selectedVerifiedQuantity)
                : UI_TEXT_EMPTY_VALUE}
            </strong>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5
        }}
      >
        <TextField
          type="text"
          label={REFUND_APPROVAL_TEXT.verifiedQtyFieldLabel}
          placeholder={
            allowsDecimals
              ? REFUND_APPROVAL_TEXT.verifiedQtyDecimalPlaceholder
              : REFUND_APPROVAL_TEXT.verifiedQtyPlaceholder
          }
          value={verifiedQuantity}
          error={Boolean(quantityError)}
          helperText={quantityError || REFUND_APPROVAL_TEXT.verifiedQtyHelper}
          onChange={onVerifiedQuantityChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            inputMode: allowsDecimals ? 'decimal' : 'numeric',
            pattern: allowsDecimals ? '[0-9]*[.]?[0-9]{0,2}' : '[0-9]*',
            min: 0,
            max: toSafeNumber(selectedOriginalQuantity)
          }}
          sx={{
            width: { xs: 'calc(100% - 42px)', sm: 210 },
            '& .MuiInputBase-root': { height: 35 },
            '& .MuiInputBase-input': { fontSize: 14 },
            '& .MuiInputLabel-root': { fontSize: 16 },
            '& .MuiFormHelperText-root': {
              mx: 0,
              fontSize: 12,
              color: quantityError ? UI_COLOR_DANGER_TEXT : UI_COLOR_TEXT_SUBTITLE,
              whiteSpace: 'nowrap'
            }
          }}
        />

        {quantityIsValid && (
          <Box
            component="span"
            role="img"
            aria-label={UI_TEXT_VALID_QUANTITY}
            sx={{ display: 'inline-flex', mt: 0.7 }}
          >
            <GoogleIcon name="check_circle" size={25} color={UI_COLOR_SUCCESS_ALT} />
          </Box>
        )}
      </Box>
    </>
  )
}

export default RefundScanSelectedLinePanel
